import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import React, { useEffect, useRef, useState } from "react";

import {
  DAC,
  DACAsArray,
  DACNodeId,
  findBestPath,
  findPathToNode,
} from "./DAGhelpers";

type NodePosition = {
  x: number;
  y: number;
  width: number;
  height: number;
  pathType: "both" | "best" | "chosen" | null;
};
type NodePositions = Record<DACNodeId, NodePosition>;
export type Path = DACNodeId[];

function convertToConcatenatedPath(simplePath: Path): Path {
  if (simplePath.length <= 1) return simplePath;

  const result: Path = [simplePath[0]]; // Keep "start" as is

  for (let i = 1; i < simplePath.length; i++) {
    const concatenatedId = simplePath.slice(1, i + 1).join("-");
    result.push(concatenatedId);
  }

  return result;
}

type Props<T> = {
  tree: DAC<T> & Record<DACNodeId, { evolutionId: number; value?: number }>;
  evolutionMap: Record<number, string>;
  handleNodeClicked: (nodeId: DACNodeId) => void;
  chosenPath?: Path | null;
};

const NODE_PADDING_X = 12;
const FONT = "14px Inter, sans-serif";
const NODE_HEIGHT = 26;
const X_GAP = 50;
const Y_GAP = 30;
const MAX_BITMAP_DIM = 32767;

export function DAGCanvas<T>({
  tree,
  evolutionMap,
  handleNodeClicked,
  chosenPath: chosenPathProp,
}: Props<T>) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const nodePositions = useRef<NodePositions>({});
  const [nodePositionsState, setNodePositionsState] = useState<NodePositions>(
    {},
  );
  const [chosenPath, setChosenPath] = useState<Path | null>(
    chosenPathProp ? convertToConcatenatedPath(chosenPathProp) : null,
  );
  const [scaleFactor, setScaleFactor] = useState(1);

  const levels = DACAsArray(tree);
  const bestPath = findBestPath(tree);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;

    // 1) reset & clear
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.font = FONT;

    // 2) measure columns
    const columnWidths: number[] = [];
    const nodeDims: Record<string, { width: number; height: number }> = {};

    levels.forEach((nodesAtLevel, ci) => {
      let maxW = 0;
      nodesAtLevel.forEach((id) => {
        const node = tree[id];
        const name = node.evolutionId
          ? (evolutionMap[node.evolutionId] ?? `#${node.evolutionId}`)
          : id;
        let label = name;
        if (node.value && node.value > 0)
          label += ` (${node.value.toFixed(1)})`;

        const w = ctx.measureText(label).width + NODE_PADDING_X * 2;
        nodeDims[id] = { width: w, height: NODE_HEIGHT };
        maxW = Math.max(maxW, w);
      });
      columnWidths[ci] = maxW;
    });

    // 3) compute nodePositions
    levels.forEach((nodesAtLevel, ci) => {
      const x0 = columnWidths
        .slice(0, ci)
        .reduce((sum, w) => sum + w + X_GAP, 0);
      nodesAtLevel.forEach((id, ri) => {
        const { width, height } = nodeDims[id];
        const y0 = ri * (NODE_HEIGHT + Y_GAP);
        const inBest = bestPath?.includes(id);
        const inChosen = chosenPath?.includes(id);

        nodePositions.current[id] = {
          x: x0,
          y: y0,
          width,
          height,
          pathType:
            inBest && inChosen
              ? "both"
              : inBest
                ? "best"
                : inChosen
                  ? "chosen"
                  : null,
        };
      });
    });
    setNodePositionsState({ ...nodePositions.current });

    // 4) compute total size
    const totalW =
      columnWidths.reduce((s, w) => s + w, 0) +
      X_GAP * (columnWidths.length - 1);
    const totalH =
      Math.max(...levels.map((l) => l.length)) * (NODE_HEIGHT + Y_GAP);

    // 5) cap & scale
    const maxW = MAX_BITMAP_DIM / dpr;
    const maxH = MAX_BITMAP_DIM / dpr;
    const scale = Math.min(1, maxW / totalW, maxH / totalH);
    setScaleFactor(scale);

    // 6) resize
    canvas.width = Math.floor(totalW * dpr * scale);
    canvas.height = Math.floor(totalH * dpr * scale);
    // CSS size must match logical content size
    canvas.style.width = `${Math.floor(totalW * scale)}px`;
    canvas.style.height = `${Math.floor(totalH * scale)}px`;

    // 7) apply scaling (no translate needed—the browser scrolls the canvas)
    ctx.scale(dpr * scale, dpr * scale);

    // 8) draw edges
    Object.entries(tree).forEach(([fromId, node]) => {
      const from = nodePositions.current[fromId];
      if (!from) return;
      node.to.forEach((toId) => {
        const to = nodePositions.current[toId];
        if (!to) return;

        const isBest =
          bestPath?.includes(fromId) &&
          bestPath.includes(toId) &&
          bestPath.indexOf(toId) === bestPath.indexOf(fromId) + 1;
        const isChosen =
          chosenPath?.includes(fromId) &&
          chosenPath.includes(toId) &&
          chosenPath.indexOf(toId) === chosenPath.indexOf(fromId) + 1;

        const style =
          isBest && isChosen
            ? "both"
            : isBest
              ? "best"
              : isChosen
                ? "chosen"
                : "none";

        const sx = from.x + from.width;
        const sy = from.y + NODE_HEIGHT / 2;
        const ex = to.x;
        const ey = to.y + NODE_HEIGHT / 2;

        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.bezierCurveTo(sx + 40, sy, ex - 40, ey, ex, ey);

        if (style === "best") {
          ctx.strokeStyle = "rgba(14, 95, 70, 0.50)";
          ctx.lineWidth = 3;
        } else if (style === "chosen") {
          ctx.strokeStyle = "#094b39";
          ctx.lineWidth = 3;
        } else {
          ctx.strokeStyle = "#49535f";
          ctx.lineWidth = 1.5;
        }
        ctx.stroke();
      });
    });
  }, [tree, chosenPath]);

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const dpr = window.devicePixelRatio || 1;
    const x = (e.clientX - rect.left) * dpr;
    const y = (e.clientY - rect.top) * dpr;

    let hit = false;
    for (const [id, pos] of Object.entries(nodePositions.current)) {
      if (
        x >= pos.x * dpr &&
        x <= (pos.x + pos.width) * dpr &&
        y >= pos.y * dpr &&
        y <= (pos.y + pos.height) * dpr
      ) {
        hit = true;
        canvasRef.current!.style.cursor = "pointer";
        break;
      }
    }
    if (!hit) {
      canvasRef.current!.style.cursor = "default";
    }
  };

  return (
    <ScrollArea className="max-h-96 h-96 px-4 touch-device:px-10 large-desktop:px-20 pt-4">
      <div className="relative max-w-content mx-auto">
        <canvas
          ref={canvasRef}
          onMouseMove={handleMouseMove}
          className="absolute top-0 left-0 z-0"
        />
        {Object.entries(nodePositionsState).map(([id, pos]) => {
          const node = tree[id as DACNodeId];
          const name = node.evolutionId
            ? (evolutionMap[node.evolutionId] ?? `#${node.evolutionId}`)
            : "Base";
          const val =
            node.value && node.value > 0 ? node.value.toFixed(1) : null;
          return (
            <button
              key={id}
              onClick={() => {
                handleNodeClicked(id as DACNodeId);
                const path = findPathToNode(tree, id as DACNodeId);
                if (path) setChosenPath(path);
              }}
              disabled={!val}
              className={cn(
                "absolute bg-action-tertiary text-dark-secondary rounded px-2 py-1 z-9 flex justify-between",
                pos.pathType === "best" &&
                  "bg-evolution-hover/50 text-[#A1C9BE]",
                (pos.pathType === "chosen" || pos.pathType === "both") &&
                  "bg-evolution-hover text-evolution-secondary",
                !val
                  ? "disabled bg-disabled text-dark-disabled cursor-not-allowed"
                  : "hover:bg-evolution-active hover:text-evolution-secondary",
              )}
              style={{
                // ← multiply by the same scaleFactor!
                left: pos.x * scaleFactor,
                top: pos.y * scaleFactor,
                width: pos.width * scaleFactor,
                height: pos.height * scaleFactor,
                fontSize: `${12 * scaleFactor}px`,
              }}
            >
              <span>{name}</span>
              {val && <span>{val}</span>}
            </button>
          );
        })}
      </div>
      <ScrollBar
        orientation="vertical"
        className="cursor-grab z-10"
        forceMount
      />
      <ScrollBar
        orientation="horizontal"
        className="cursor-grab z-10"
        forceMount
      />
    </ScrollArea>
  );
}
